import type { MutableRefObject } from 'react';

import { FormTable } from '@connected/form-fields/components/form-table';
import { useMemo } from 'react';

import { useCondition } from '@/config/conditions';

import type { DeviceActions } from '../../distribution-board-device/hooks';
import type { DeviceFormValues } from '../../distribution-board-device/types/hook-types';

import { useFinalCircuitProtectiveDevice } from '../../distribution-board-device/hooks';
import { createFCPDColumns } from './columns';
import { ExpandedImpedanceContent } from './expanded-impedance-content';

type FCPDDeviceTableProps = {
    electricalProjectVersionId: string;
    electricalProjectDistributionBoardId: string;
    currentPage?: number;
    onPageChange?: (page: number) => void;
    /**
     * Optional ref to expose device actions (create, copy, delete) to the parent.
     * Useful for placing the create button in the route's Section.Header.
     */
    actionsRef?: MutableRefObject<DeviceActions | null>;
};

export function FCPDDeviceTable({
    electricalProjectVersionId,
    electricalProjectDistributionBoardId,
    currentPage,
    onPageChange,
    actionsRef,
}: FCPDDeviceTableProps) {
    // Use conditions to determine feature flags
    const enableExtendedRiso = useCondition('canAccessExtendedRiso');
    const enableImpedanceMeasurementsPage = useCondition('canAccessImpedanceMeasurementsPage');

    const hookResult = useFinalCircuitProtectiveDevice({
        electricalProjectVersionId,
        electricalProjectDistributionBoardId,
        enableExtendedRiso,
        enableImpedanceMeasurementsPage,
    });

    // Expose actions to parent via ref if provided
    if (actionsRef) {
        actionsRef.current = hookResult.actions;
    }

    const rowProps = {
        electricalProjectVersionId,
        electricalProjectDistributionBoardId,
        page: currentPage ?? 1,
    };

    // Create column definitions
    const columns = useMemo(
        () => createFCPDColumns({
            form: hookResult.form,
            createPatcher: hookResult.createPatcher,
            getFieldOptions: hookResult.getFieldOptions,
            isFieldDisabled: hookResult.isFieldDisabled,
            actions: hookResult.actions,
            enableExtendedRiso,
            enableImpedanceMeasurementsPage,
            rowProps,
        }),
        [
            hookResult.form,
            hookResult.createPatcher,
            hookResult.getFieldOptions,
            hookResult.isFieldDisabled,
            hookResult.actions,
            enableExtendedRiso,
            enableImpedanceMeasurementsPage,
            rowProps,
        ],
    );

    if (hookResult.devices.length === 0) {
        return <div className="text-center py-4 text-muted-foreground">Geen apparaten gevonden</div>;
    }

    return (
        <FormTable<{ devices: DeviceFormValues[] }, 'devices'>
            key={hookResult.formKey}
            form={hookResult.form as any}
            fieldArrayName="devices"
            columns={columns}
            pageSize={10}
            currentPage={currentPage}
            onPageChange={onPageChange}
            emptyMessage="Geen apparaten gevonden"
            getRowKey={row => row.id}
            expansion={!enableImpedanceMeasurementsPage
                ? {
                        type: 'single',
                        collapsible: true,
                        renderExpanded: props => (
                            <ExpandedImpedanceContent
                                {...props}
                                createPatcher={hookResult.createPatcher}
                            />
                        ),
                    }
                : undefined}
            controlsColumn={!enableImpedanceMeasurementsPage
                ? {
                        showExpand: true,
                    }
                : undefined}
        />
    );
}
